// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { inject, Injectable } from '@angular/core';
import { MapApiService } from '../../shared/services/map-api.service';
import { getSparePartsOverview } from '../../../api/remote-api/.spareparts.generated/fn/spare-parts/get-spare-parts-overview';
import { paramObjectKeyerDefault } from '../../shared/utils/api-call-state.util';
import { SpTicketStatus, SpTicketType } from '../enum/spare-parts-enums';
import { map } from 'rxjs';
import { SpPartsModel } from '../interfaces/sp-parts-model';
import { getHighestConsumedPart } from 'src/app/api/remote-api/.spareparts.generated/fn/spare-parts/get-highest-consumed-part';

@Injectable({
  providedIn: 'root',
})
export class SparePartsOverviewService {
  private readonly mapApiService = inject(MapApiService);

  public getSparePartsOverview = this.mapApiService.mapEndpoint(
    getSparePartsOverview,
    {
      customCacheKey: paramObjectKeyerDefault,
    },
  );

  public getHighestConsumedPart = this.mapApiService.mapEndpoint(
    getHighestConsumedPart,
  );

  public mostConsumedPart$ = this.getHighestConsumedPart.lastSuccessfulData$;

  public getAllTickets$ = this.getSparePartsOverview.lastSuccessfulData$.pipe(
    map((data) => data.tickets),
  );

  public getServiceTickets$ = this.getAllTickets$.pipe(
    map((data) => data?.filter((t) => this.isServiceTicket(t))),
  );

  public getServiceParts$ = this.getAllTickets$.pipe(
    map((tickets) => {
      const result = (tickets ?? [])
        .map((ticket) =>
          (ticket.parts ?? []).map((part) => {
            return {
              ...part,
              ticketType: ticket.ticketType,
              ticketStatus: ticket.status,
            };
          }),
        )
        .reduce(
          (accumulator, current) => accumulator?.concat(current as any),
          [] as SpPartsModel[],
        );

      return result;
    }),
  );

  private isServiceTicket(ticket: {
    ticketType?: string | null;
    status?: string | null;
  }) {
    return (
      ticket.ticketType === SpTicketType.ServiceTicket ||
      ticket.status === SpTicketStatus.Closed
    );
  }
}
